import { useCallback, useEffect, useMemo, useState } from "react";
import { listRows, updateRow } from "@/lib/adminApi";
import { SETTLEMENT_STATUS, metaFor, optionsFrom, type FarmerSettlement } from "@/lib/adminModules";
import {
  DataTable,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  btnAccent,
  btnGhost,
  btnPrimary,
  fieldInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { exportToPdf } from "@/pages/admin/exportUtils";
import { formatRupees } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

export default function AdminFarmerSettlements() {
  const [rows, setRows] = useState<FarmerSettlement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<FarmerSettlement>("farmer_settlements", "final_settlement"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load settlements.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<FarmerSettlement>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("farmer_settlements", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.farmer_name, row.farm_name || "", row.period || ""].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, statusFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const totals = useMemo(() => {
    const gross = rows.reduce((sum, row) => sum + Number(row.gross_booking || 0), 0);
    const commission = rows.reduce((sum, row) => sum + Number(row.commission || 0), 0);
    const pending = rows
      .filter((row) => row.status === "pending" || row.status === "ready" || row.status === "processing")
      .reduce((sum, row) => sum + Number(row.final_settlement || 0), 0);
    const paid = rows
      .filter((row) => row.status === "paid")
      .reduce((sum, row) => sum + Number(row.final_settlement || 0), 0);
    return { gross, commission, pending, paid };
  }, [rows]);

  const statementPdf = (row: FarmerSettlement) => {
    exportToPdf({
      title: `Settlement Statement — ${row.farmer_name}`,
      subtitle: `${row.farm_name || ""} · ${row.period || ""}`,
      columns: [
        { header: "Line item", value: (item) => item.label },
        { header: "Amount", value: (item) => item.amount },
      ],
      rows: [
        { label: "Gross booking value", amount: formatRupees(row.gross_booking) },
        { label: "Rythugaru commission", amount: `- ${formatRupees(row.commission)}` },
        { label: "Operational costs", amount: `- ${formatRupees(row.operational_costs)}` },
        { label: "Refund adjustments", amount: formatRupees(row.refund_adjustments) },
        { label: "Farmer share", amount: formatRupees(row.farmer_share) },
        { label: "Final settlement", amount: formatRupees(row.final_settlement) },
      ],
    });
  };

  const columns: Column<FarmerSettlement>[] = [
    {
      key: "farmer",
      header: "Farmer",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.farmer_name}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{row.period || "—"}</p>
        </div>
      ),
    },
    {
      key: "farm",
      header: "Farm",
      render: (row) => <span className="text-sm text-foreground-700">{row.farm_name || "—"}</span>,
    },
    {
      key: "gross",
      header: "Gross",
      render: (row) => (
        <span className="text-sm text-foreground-700 whitespace-nowrap">{formatRupees(row.gross_booking)}</span>
      ),
    },
    {
      key: "commission",
      header: "Commission",
      render: (row) => (
        <span className="text-sm text-foreground-700 whitespace-nowrap">{formatRupees(row.commission)}</span>
      ),
    },
    {
      key: "final",
      header: "Final settlement",
      render: (row) => (
        <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">
          {formatRupees(row.final_settlement)}
        </span>
      ),
    },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(SETTLEMENT_STATUS, row.status)} /> },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Farm Network"
        title="Farmer Settlements"
        subtitle="Work out each farmer's share — gross bookings, commission, costs and refunds — then approve and pay."
      />

      <StatCards
        stats={[
          { label: "Gross bookings", value: formatRupees(totals.gross), icon: "ri-line-chart-line", tone: "primary" },
          { label: "Rythugaru commission", value: formatRupees(totals.commission), icon: "ri-percent-line", tone: "secondary" },
          { label: "Pending payout", value: formatRupees(totals.pending), icon: "ri-time-line", tone: "accent" },
          { label: "Paid out", value: formatRupees(totals.paid), icon: "ri-checkbox-circle-line", tone: "primary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by farmer, farm, period…" />
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: "all", label: "All statuses" }, ...optionsFrom(SETTLEMENT_STATUS)]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">
            {filtered.length} settlements
          </span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading settlements…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-hand-coin-line"
            title={rows.length === 0 ? "No settlements yet" : "No settlements match"}
            message="Monthly settlements will be generated here once bookings close."
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.farmer_name || "Settlement"}
        subtitle={selected?.period || "Settlement"}
        footer={
          selected ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => update(selected.id, { status: "processing" }, "Settlement processing.")}
                className={btnPrimary}
              >
                <i className="ri-play-circle-line"></i> Approve &amp; process
              </button>
              <button
                type="button"
                onClick={() => update(selected.id, { status: "paid" }, "Settlement marked as paid.")}
                className={btnAccent}
              >
                <i className="ri-check-double-line"></i> Mark paid
              </button>
              <button type="button" onClick={() => statementPdf(selected)} className={btnGhost}>
                <i className="ri-file-download-line"></i> Statement
              </button>
            </div>
          ) : null
        }
      >
        {selected ? (
          <>
            <StatusPill {...metaFor(SETTLEMENT_STATUS, selected.status)} />

            <div className="bg-background-100 border border-background-200 rounded-md divide-y divide-background-200">
              {[
                { label: "Gross booking value", value: selected.gross_booking, sign: "" },
                { label: "Rythugaru commission", value: selected.commission, sign: "-" },
                { label: "Operational costs", value: selected.operational_costs, sign: "-" },
                { label: "Refund adjustments", value: selected.refund_adjustments, sign: "" },
              ].map((line) => (
                <div key={line.label} className="flex items-center justify-between px-4 py-3 text-sm">
                  <span className="text-foreground-600">{line.label}</span>
                  <span className="text-foreground-900 font-medium">
                    {line.sign} {formatRupees(line.value)}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm font-semibold text-foreground-950">Farmer share</span>
                <span className="text-sm font-semibold text-primary-700">{formatRupees(selected.farmer_share)}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-3 bg-secondary-50">
                <span className="text-sm font-semibold text-foreground-950">Final settlement</span>
                <span className="font-heading text-lg font-semibold text-foreground-950">
                  {formatRupees(selected.final_settlement)}
                </span>
              </div>
            </div>

            <Field label="Status">
              <select
                value={selected.status}
                onChange={(event) => update(selected.id, { status: event.target.value })}
                className={fieldInput}
              >
                {optionsFrom(SETTLEMENT_STATUS).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Download a settlement statement for your records.
      </p>
      {node}
    </div>
  );
}